import { useEffect, useRef, useState, type ReactNode } from "react";

interface DeferMountProps {
  children: ReactNode;
  /** Reserved space before the real content mounts, to avoid layout shift. */
  minHeight?: number;
  /** How far ahead of the viewport to start loading. */
  rootMargin?: string;
}

/**
 * Mounts children only when they approach the viewport.
 *
 * Lazy route chunks are requested the moment their component renders, so a
 * page full of `React.lazy` sections still downloads every chunk during the
 * initial load and competes with the hero image for bandwidth. Gating the
 * render on an IntersectionObserver keeps those requests off the critical
 * path entirely.
 */
export function DeferMount({ children, minHeight = 320, rootMargin = "400px" }: DeferMountProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  if (show) return <>{children}</>;
  return <div ref={ref} style={{ minHeight }} aria-hidden="true" />;
}
